import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MifSubscriptionAuthService } from '@shared/auth/mif-subscription.auth.service';
import { ResumeAuthService } from '@shared/auth/resume-auth.service';
import { AuthorizationDeliveryMode } from '@shared/webapi/MIF.Subscription.WebApi';
import { CustomerService } from '@shared/webapi/services/customer.service';
import { AUTH_TOO_MANY_REQUESTS_MESSAGE } from '../../../constants/auth-messages';
import { parseRequestCodeClientError } from '../../../../store/client-store/client.helpers';
import { ClientFacade } from '../../../../store/client-store/client.facade';
import { ClientAuthErrorCode, ClientAuthorizationStatus } from '../../../../store/client-store/client.models';

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'app-mif-login',
  templateUrl: './mif-login.component.html',
  styleUrls: ['./mif-login.component.scss']
})
export class MifLoginComponent implements OnInit {
  userId: string | null = null;
  sessionId: string | null = null;
  returnUrl: string | null = null;
  otpRequestFailed = false;
  showTooManyRequestsScreen = false;
  showOtpCheck = false;
  readonly tooManyRequestsMessage = AUTH_TOO_MANY_REQUESTS_MESSAGE;
  private isNavigatingAfterAuthorization = false;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly mifSubscriptionAuthService: MifSubscriptionAuthService,
    private readonly clientFacade: ClientFacade,
    private readonly customerService: CustomerService,
    private readonly resumeAuthService: ResumeAuthService
  ) {}

  ngOnInit(): void {
    this.clientFacade.cancelClientAuthentication();
    this.initClientAuthorizationSubscription();
    this.userId = this.route.snapshot.queryParamMap.get('userId');
    this.returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');

    if (!this.userId) {
      return;
    }

    this.customerService.userId = this.userId;

    this.mifSubscriptionAuthService
      .resumeSessionByUserId(this.userId, AuthorizationDeliveryMode.Sms)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: authResponse => {
          this.sessionId = authResponse.sessionId || null;
          this.showOtpCheck = !!this.sessionId;
          this.showTooManyRequestsScreen = false;
          this.otpRequestFailed = !this.showOtpCheck;
        },
        error: error => {
          const authError = parseRequestCodeClientError(error);

          this.showOtpCheck = false;
          this.showTooManyRequestsScreen = authError.errorCode === ClientAuthErrorCode.TooManyRequests;
          this.otpRequestFailed = !this.showTooManyRequestsScreen;
        }
      });
  }

  private initClientAuthorizationSubscription(): void {
    this.clientFacade.clientAuthorizationStatus$
      .pipe(untilDestroyed(this))
      .subscribe(async clientAuthorizationStatus => {
        if (
          clientAuthorizationStatus !== ClientAuthorizationStatus.Authorized ||
          this.isNavigatingAfterAuthorization ||
          !this.userId
        ) {
          return;
        }

        this.isNavigatingAfterAuthorization = true;

        try {
          await this.resumeAuthService.navigateAfterAuthorization(this.userId, this.returnUrl);
        } catch (error) {
          await this.customerService.clearAllData();

          if (this.resumeAuthService.isUnauthorizedError(error)) {
            await this.resumeAuthService.redirectToResume(this.userId, this.returnUrl);
            return;
          }

          await this.router.navigate(['subscription-time-end']);
        } finally {
          this.isNavigatingAfterAuthorization = false;
        }
      });
  }
}